import { onBeforeUnmount, ref, watch, type Ref } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')
const BLOCKED_ANCESTOR_SELECTOR =
  '[hidden], [inert], [aria-hidden="true"], [data-overlay-order="-1"]'

export interface OverlayFocusReturnOptions {
  resolveFallback?: (source: HTMLElement | null) => HTMLElement | null
}

export interface OverlayFocusReturn {
  focusScope: Ref<HTMLElement | null>
  setFocusScope: (element: unknown) => void
  completeLeave: () => void
}

function isVisible(element: HTMLElement): boolean {
  let current: HTMLElement | null = element
  while (current) {
    const style = window.getComputedStyle(current)
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse'
    ) {
      return false
    }
    current = current.parentElement
  }
  return true
}

function canReceiveFocus(element: HTMLElement | null): element is HTMLElement {
  return Boolean(
    element?.isConnected &&
    !element.matches(':disabled, [aria-disabled="true"]') &&
    !element.closest(BLOCKED_ANCESTOR_SELECTOR) &&
    isVisible(element),
  )
}

function firstScopeFallback(scope: HTMLElement | null): HTMLElement | null {
  if (!canReceiveFocus(scope)) return null
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].find(canReceiveFocus) ?? null
}

function currentFocusAllowsRestore(closingScope: HTMLElement | null): boolean {
  const activeElement = document.activeElement
  if (!(activeElement instanceof HTMLElement) || activeElement === document.body) return true
  if (!canReceiveFocus(activeElement)) return true
  return Boolean(closingScope?.contains(activeElement))
}

/**
 * Owns focus return for transitioned overlay primitives.
 * BODY is intentionally the last-resort fallback when neither source nor a document candidate
 * remains safe; in that case no focus call is made and the browser keeps BODY active.
 */
export function useOverlayFocusReturn(
  active: Readonly<Ref<boolean>>,
  options: OverlayFocusReturnOptions = {},
): OverlayFocusReturn {
  const focusScope = ref<HTMLElement | null>(null)
  let source: HTMLElement | null = null
  let parentScope: HTMLElement | null = null
  let closingScope: HTMLElement | null = null
  let cycle = 0
  let completedCycle = 0
  let disposed = false

  function setFocusScope(element: unknown): void {
    focusScope.value = element instanceof HTMLElement ? element : null
  }

  function capture(): void {
    cycle += 1
    const activeElement = document.activeElement
    source =
      activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null
    parentScope =
      source?.closest<HTMLElement>(
        '[data-overlay-kind][data-overlay-order]:not([data-overlay-order="-1"])',
      ) ?? null
    closingScope = null
  }

  function restore(capturedCycle: number, capturedSource: HTMLElement | null): void {
    if (
      (!disposed && active.value) ||
      capturedCycle !== cycle ||
      completedCycle === capturedCycle
    ) {
      return
    }
    completedCycle = capturedCycle
    if (currentFocusAllowsRestore(closingScope)) {
      const fallback = options.resolveFallback?.(capturedSource) ?? null
      const target = canReceiveFocus(capturedSource)
        ? capturedSource
        : canReceiveFocus(fallback)
          ? fallback
          : firstScopeFallback(parentScope)
      target?.focus({ preventScroll: true })
    }
    source = null
    parentScope = null
    closingScope = null
  }

  function completeLeave(): void {
    const capturedCycle = cycle
    const capturedSource = source
    if (capturedCycle === 0 || completedCycle === capturedCycle) return
    queueMicrotask(() => restore(capturedCycle, capturedSource))
  }

  watch(
    active,
    (isActive, wasActive) => {
      if (isActive && !wasActive) {
        if (cycle === completedCycle) capture()
        else closingScope = null
      }
      if (!isActive && wasActive) closingScope = focusScope.value
    },
    { flush: 'sync', immediate: true },
  )

  onBeforeUnmount(() => {
    disposed = true
    closingScope = focusScope.value
    completeLeave()
  })

  return { focusScope, setFocusScope, completeLeave }
}
